import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { MsgAction } from '../store/actions';

/**
 * Whether a group has a call going on right now.
 *
 * This exists because a group call used to have exactly one entry point — the FCM ring —
 * and that ring is a one-shot. Decline it, miss the sixty-second window, close the tab,
 * or simply be offline when it fired, and there was no route back in: the group header
 * showed no sign that a call was happening, and pressing its call button minted a
 * SECOND channel, leaving the group split across two rooms with neither side able to see
 * the other.
 *
 * Two sources, in this order, because neither alone is enough:
 *
 *   • **A fetch when the group is opened.** Authoritative. A socket can be reconnecting,
 *     or the client can have been offline when the call started, so the state has to be
 *     readable on demand and not only pushed.
 *   • **`call:group-state` over the socket.** Keeps that answer fresh while the group
 *     stays open, so a call starting a moment later still surfaces.
 *
 * Addressed to each member's own user room server-side, not to the channel room — a
 * socket only joins a channel room while that conversation is open, and the whole point
 * is to reach people who are looking at something else.
 *
 * Deliberately local state rather than a Redux slice: the group header is the only
 * consumer today. Promote it if the conversation list wants the same badge.
 *
 * @param {object|null} socket the shared Socket.IO client
 * @param {string|null} groupId the group currently on screen
 * @returns {{activeCall: {call_id: string, type: string, answered_at: string|null}|null, refresh: Function}}
 */
export const useGroupCall = (socket, groupId) => {
  const dispatch = useDispatch();
  const [activeCall, setActiveCall] = useState(null);

  const refresh = useCallback(
    (onDone) => {
      if (!groupId) return;
      dispatch(MsgAction.ActiveGroupCall(groupId, (data) => onDone?.(data ?? null)));
    },
    [dispatch, groupId]
  );

  // Authoritative read, re-run on every group change. Cleared first so a stale "Join"
  // from the previous group cannot flash on the new one, and `alive` drops a response
  // that lands after the user has already moved on.
  useEffect(() => {
    setActiveCall(null);
    if (!groupId) return undefined;

    let alive = true;
    dispatch(
      MsgAction.ActiveGroupCall(groupId, (data) => {
        if (alive) setActiveCall(data ?? null);
      })
    );

    return () => {
      alive = false;
    };
  }, [groupId, dispatch]);

  // Live updates while this group is on screen.
  useEffect(() => {
    if (!socket || !groupId) return undefined;

    const onGroupCallState = (payload) => {
      // The event goes to every group this user belongs to, so it has to be filtered —
      // otherwise a call in one group would light up the header of another.
      if (String(payload?.group_id) !== String(groupId)) return;

      setActiveCall(
        payload.active ? { call_id: payload.call_id, type: payload.type, answered_at: null } : null
      );
    };

    socket.on('call:group-state', onGroupCallState);
    return () => socket.off('call:group-state', onGroupCallState);
  }, [socket, groupId]);

  return { activeCall, refresh };
};

export default useGroupCall;
